import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Icon,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import {
  MdAdd,
  MdChevronRight,
  MdEdit,
  MdExpandMore,
  MdApartment,
  MdLocationOn,
} from 'react-icons/md';
import {
  AppCard,
  AppInput,
  OutlineButton,
  PrimaryButton,
  SortableTh,
  StatusBadge,
  PageLoader,
} from '../../../../components/ui';
import { useTableSort } from '../../../../hooks/useTableSort';
import { VillageSelect } from '../../../../components/ui/VillageSelect';
import { ActiveBadge } from '../MasterDataPage';
import { findVillageById } from '../../../../data/master/villages';
import {
  createBusinessUnit,
  createSite,
  listBusinessUnits,
  listSites,
  updateBusinessUnit,
  updateSite,
} from '../../../../repositories/businessUnit.repository';
import { lookupVillages } from '../../../../repositories/lookup.repository';

const emptyBuForm = {
  shortName: '',
  fullNameGst: '',
  address1: '',
  address2: '',
  villageId: '',
  active: true,
};

const emptySiteForm = {
  buId: '',
  siteName: '',
  projectOperation: '',
  address1: '',
  address2: '',
  villageId: '',
  pinOverride: '',
  active: true,
};

function VillageCell({ villageId, villageDbId, villageName, villages }) {
  const v =
    findVillageById(villages, villageId) ||
    findVillageById(villages, villageDbId);
  if (!v) {
    if (villageName) {
      return (
        <Box>
          <Text fontSize="13px" fontWeight="600">
            {villageName}
          </Text>
          {villageId || villageDbId ? (
            <Text fontSize="12px" color="text.secondary">
              {villageId || `#${villageDbId}`}
            </Text>
          ) : null}
        </Box>
      );
    }
    return (
      <Text fontSize="13px" color="text.secondary">
        {villageId || (villageDbId != null ? `#${villageDbId}` : '—')}
      </Text>
    );
  }
  return (
    <Box>
      <Text fontSize="13px" fontWeight="600">
        {v.villageName}
      </Text>
      <Text fontSize="12px" color="text.secondary">
        {v.villageId} · {v.talukaName} · {v.pin}
      </Text>
    </Box>
  );
}

export default function BusinessUnitsPage() {
  const toast = useToast();
  const [villages, setVillages] = useState([]);
  const [bus, setBus] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const {
    sorted: sortedBus,
    sort,
    toggleSort,
  } = useTableSort(bus, {
    buName: (b) => b.buName || b.shortName,
  });
  const [expanded, setExpanded] = useState(() => new Set());
  const [mode, setMode] = useState('bu'); // bu | site
  const [editingId, setEditingId] = useState(null);
  const [editingCode, setEditingCode] = useState(null);
  const [buForm, setBuForm] = useState(emptyBuForm);
  const [siteForm, setSiteForm] = useState(emptySiteForm);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const loadAll = useCallback(
    async ({ showSpinner = true } = {}) => {
      if (showSpinner) setLoading(true);
      try {
        const [buResult, siteResult, villageOptions] = await Promise.all([
          listBusinessUnits({ pageSize: 500 }),
          listSites({ pageSize: 500 }),
          lookupVillages(),
        ]);
        const nextBus = buResult.items || [];
        const nextSites = siteResult.items || [];
        setBus(nextBus);
        setSites(nextSites);
        setVillages(villageOptions || []);
        setExpanded((prev) => {
          if (prev.size > 0) return prev;
          return new Set(nextBus.filter((b) => b.buId).map((b) => b.buId));
        });
      } catch (err) {
        toast({
          title: err?.message || 'Failed to load business units',
          status: 'error',
          position: 'top-right',
        });
        setBus([]);
        setSites([]);
        setVillages([]);
      } finally {
        if (showSpinner) setLoading(false);
      }
    },
    [toast],
  );

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const sitesByBu = useMemo(() => {
    const map = {};
    sites.forEach((s) => {
      const keys = [s.buId, s.buDbId].filter((k) => k != null && k !== '');
      keys.forEach((key) => {
        if (!map[key]) map[key] = [];
        if (!map[key].includes(s)) map[key].push(s);
      });
    });
    return map;
  }, [sites]);

  const resolveVillageCode = (row) => {
    if (row?.villageId) return row.villageId;
    const village = findVillageById(villages, row?.villageDbId);
    return village?.villageId || '';
  };

  const resolveVillageDbId = (villageCodeOrId) => {
    const village = findVillageById(villages, villageCodeOrId);
    return village?.id ?? null;
  };

  const openAddBu = () => {
    setMode('bu');
    setEditingId(null);
    setEditingCode(null);
    setBuForm(emptyBuForm);
    onOpen();
  };

  const openEditBu = (bu) => {
    setMode('bu');
    setEditingId(bu.id);
    setEditingCode(bu.buId || null);
    setBuForm({
      shortName: bu.shortName || '',
      fullNameGst: bu.fullNameGst || '',
      address1: bu.address1 || '',
      address2: bu.address2 || '',
      villageId: resolveVillageCode(bu),
      active: bu.active !== false,
    });
    onOpen();
  };

  const openAddSite = (bu) => {
    setMode('site');
    setEditingId(null);
    setEditingCode(null);
    setSiteForm({
      ...emptySiteForm,
      buId: bu.buId,
      villageId: resolveVillageCode(bu),
    });
    onOpen();
  };

  const openEditSite = (site) => {
    setMode('site');
    setEditingId(site.id);
    setEditingCode(site.siteId || null);
    setSiteForm({
      buId: site.buId,
      siteName: site.siteName || '',
      projectOperation: site.projectOperation || '',
      address1: site.address1 || '',
      address2: site.address2 || '',
      villageId: resolveVillageCode(site),
      pinOverride: site.pinOverride || '',
      active: site.active !== false,
    });
    onOpen();
  };

  const toggle = (buId) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(buId)) next.delete(buId);
      else next.add(buId);
      return next;
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (mode === 'bu') {
      if (!buForm.shortName.trim() || !buForm.villageId) {
        toast({
          title: 'Short name and Village are required',
          status: 'warning',
          position: 'top-right',
        });
        return;
      }
      const villageDbId = resolveVillageDbId(buForm.villageId);
      if (villageDbId == null) {
        toast({
          title: 'Selected village is invalid',
          status: 'warning',
          position: 'top-right',
        });
        return;
      }
      const payload = {
        shortName: buForm.shortName.trim(),
        fullNameGst: buForm.fullNameGst.trim(),
        address1: buForm.address1.trim(),
        address2: buForm.address2.trim(),
        villageId: buForm.villageId,
        villageDbId,
        active: buForm.active,
      };
      setSaving(true);
      try {
        if (editingId != null) {
          await updateBusinessUnit(editingId, payload);
          toast({
            title: 'Business Unit updated',
            description: `${payload.shortName}${editingCode ? ` · ${editingCode}` : ''}`,
            status: 'success',
            position: 'top-right',
          });
        } else {
          const created = await createBusinessUnit(payload);
          toast({
            title: 'Business Unit saved',
            description: `${payload.shortName}${created?.buId ? ` · ${created.buId}` : ''}`,
            status: 'success',
            position: 'top-right',
          });
        }
        onClose();
        await loadAll({ showSpinner: false });
      } catch (err) {
        toast({
          title: err?.message || 'Failed to save business unit',
          status: 'error',
          position: 'top-right',
        });
      } finally {
        setSaving(false);
      }
      return;
    }

    if (!siteForm.siteName.trim() || !siteForm.buId || !siteForm.villageId) {
      toast({
        title: 'Site name, BU and Village are required',
        status: 'warning',
        position: 'top-right',
      });
      return;
    }
    const parentBu = bus.find((b) => b.buId === siteForm.buId);
    const buDbId = parentBu?.id ?? null;
    const villageDbId = resolveVillageDbId(siteForm.villageId);
    if (buDbId == null) {
      toast({
        title: 'Selected business unit is invalid',
        status: 'warning',
        position: 'top-right',
      });
      return;
    }
    if (villageDbId == null) {
      toast({
        title: 'Selected village is invalid',
        status: 'warning',
        position: 'top-right',
      });
      return;
    }
    const payload = {
      buId: siteForm.buId,
      buDbId,
      siteName: siteForm.siteName.trim(),
      projectOperation: siteForm.projectOperation.trim(),
      address1: siteForm.address1.trim(),
      address2: siteForm.address2.trim(),
      villageId: siteForm.villageId,
      villageDbId,
      pinOverride: siteForm.pinOverride.trim(),
      active: siteForm.active,
    };
    setSaving(true);
    try {
      if (editingId != null) {
        await updateSite(editingId, payload);
        toast({
          title: 'Site updated',
          description: `${payload.siteName}${editingCode ? ` · ${editingCode}` : ''}`,
          status: 'success',
          position: 'top-right',
        });
      } else {
        const created = await createSite(payload);
        setExpanded((prev) => new Set([...prev, siteForm.buId]));
        toast({
          title: 'Site saved',
          description: `${payload.siteName}${created?.siteId ? ` · ${created.siteId}` : ''}`,
          status: 'success',
          position: 'top-right',
        });
      }
      onClose();
      await loadAll({ showSpinner: false });
    } catch (err) {
      toast({
        title: err?.message || 'Failed to save site',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <PageLoader label="Loading business units…" />;
  }

  return (
    <Box>
      <Flex
        justify="space-between"
        align={{ base: 'start', md: 'center' }}
        gap={4}
        mb={6}
        wrap="wrap"
      >
        <Box>
          <Heading size="lg">Business Unit & Site</Heading>
          <Text color="text.secondary" mt={2} maxW="720px">
            Two layers only: <Text as="span" fontWeight="600">Business Unit</Text> →{' '}
            <Text as="span" fontWeight="600">Site</Text>. Both store a Village ID from Village Master.
          </Text>
        </Box>
        <PrimaryButton size="sm" leftIcon={<MdAdd />} onClick={openAddBu}>
          Add Business Unit
        </PrimaryButton>
      </Flex>

      <AppCard p={0} overflow="hidden">
        <Box overflowX="auto">
          <Table variant="simple" size="md">
            <Thead bg="bg.input">
              <Tr>
                <SortableTh columnKey="buName" sort={sort} onSort={toggleSort}>
                  Name
                </SortableTh>
                <SortableTh columnKey="buId" sort={sort} onSort={toggleSort}>
                  ID
                </SortableTh>
                <Th>Type</Th>
                <SortableTh columnKey="villageId" sort={sort} onSort={toggleSort}>
                  Village
                </SortableTh>
                <SortableTh columnKey="active" sort={sort} onSort={toggleSort}>
                  Status
                </SortableTh>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {sortedBus.map((bu) => {
                const children = sitesByBu[bu.buId] || sitesByBu[bu.id] || [];
                const isRowOpen = expanded.has(bu.buId);
                return (
                  <React.Fragment key={bu.id ?? bu.buId}>
                    <Tr _hover={{ bg: 'bg.input' }}>
                      <Td>
                        <Flex align="center" gap={1}>
                          {children.length ? (
                            <IconButton
                              aria-label={isRowOpen ? 'Collapse' : 'Expand'}
                              icon={
                                <Icon as={isRowOpen ? MdExpandMore : MdChevronRight} boxSize={5} />
                              }
                              size="xs"
                              variant="ghostInk"
                              onClick={() => toggle(bu.buId)}
                            />
                          ) : (
                            <Box w="28px" />
                          )}
                          <Icon as={MdApartment} color="utkarsh.amber" boxSize={4} mr={1} />
                          <Box>
                            <Text fontWeight="700">{bu.shortName}</Text>
                            <Text fontSize="12px" color="text.secondary" noOfLines={1}>
                              {bu.fullNameGst || '—'}
                            </Text>
                          </Box>
                        </Flex>
                      </Td>
                      <Td fontFamily="mono" fontSize="13px">
                        {bu.buId}
                      </Td>
                      <Td>
                        <StatusBadge status="info">Business Unit</StatusBadge>
                      </Td>
                      <Td>
                        <VillageCell
                          villageId={bu.villageId}
                          villageDbId={bu.villageDbId}
                          villageName={bu.villageName}
                          villages={villages}
                        />
                      </Td>
                      <Td>
                        <ActiveBadge active={bu.active} />
                      </Td>
                      <Td>
                        <HStack spacing={2}>
                          <IconButton
                            aria-label={`Edit ${bu.shortName}`}
                            icon={<MdEdit />}
                            size="sm"
                            variant="ghostInk"
                            onClick={() => openEditBu(bu)}
                          />
                          <OutlineButton
                            size="sm"
                            leftIcon={<MdAdd />}
                            onClick={() => openAddSite(bu)}
                          >
                            Add site
                          </OutlineButton>
                        </HStack>
                      </Td>
                    </Tr>
                    {isRowOpen
                      ? children.map((site) => (
                          <Tr key={site.id ?? site.siteId} _hover={{ bg: 'bg.input' }}>
                            <Td>
                              <Flex align="center" pl="48px" gap={2}>
                                <Icon as={MdLocationOn} color="text.secondary" boxSize={4} />
                                <Box>
                                  <Text fontWeight="600">{site.siteName}</Text>
                                  <Text fontSize="12px" color="text.secondary">
                                    {site.projectOperation || '—'}
                                  </Text>
                                </Box>
                              </Flex>
                            </Td>
                            <Td fontFamily="mono" fontSize="13px">
                              {site.siteId}
                            </Td>
                            <Td>
                              <StatusBadge status="verified">Site</StatusBadge>
                            </Td>
                            <Td>
                              <VillageCell
                                villageId={site.villageId}
                                villageDbId={site.villageDbId}
                                villageName={site.villageName}
                                villages={villages}
                              />
                            </Td>
                            <Td>
                              <ActiveBadge active={site.active} />
                            </Td>
                            <Td>
                              <IconButton
                                aria-label={`Edit ${site.siteName}`}
                                icon={<MdEdit />}
                                size="sm"
                                variant="ghostInk"
                                onClick={() => openEditSite(site)}
                              />
                            </Td>
                          </Tr>
                        ))
                      : null}
                  </React.Fragment>
                );
              })}
              {sortedBus.length === 0 ? (
                <Tr>
                  <Td colSpan={6}>
                    <Text color="text.secondary" py={6} textAlign="center">
                      No business units yet.
                    </Text>
                  </Td>
                </Tr>
              ) : null}
            </Tbody>
          </Table>
        </Box>
      </AppCard>

      <Modal
        isOpen={isOpen}
        onClose={() => {
          if (!saving) onClose();
        }}
        size="xl"
        isCentered
        scrollBehavior="inside"
      >
        <ModalOverlay bg="blackAlpha.500" />
        <ModalContent bg="bg.surface" borderRadius="md" mx={4}>
          <ModalHeader>
            {mode === 'bu'
              ? editingId != null
                ? `Edit Business Unit · ${editingCode || editingId}`
                : 'Add Business Unit'
              : editingId != null
                ? `Edit Site · ${editingCode || editingId}`
                : 'Add Site'}
          </ModalHeader>
          <ModalCloseButton isDisabled={saving} />
          <Box as="form" onSubmit={handleSave}>
            <ModalBody>
              {mode === 'bu' ? (
                <VStack align="stretch" spacing={4}>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <AppInput
                      label="Short Name"
                      isRequired
                      placeholder="e.g. APMUL"
                      value={buForm.shortName}
                      onChange={(e) => setBuForm((f) => ({ ...f, shortName: e.target.value }))}
                    />
                    <AppInput
                      label="Full Name as per GST"
                      placeholder="e.g. Adani Power Mundra Limited"
                      value={buForm.fullNameGst}
                      onChange={(e) => setBuForm((f) => ({ ...f, fullNameGst: e.target.value }))}
                    />
                    <AppInput
                      label="Address 1"
                      value={buForm.address1}
                      onChange={(e) => setBuForm((f) => ({ ...f, address1: e.target.value }))}
                    />
                    <AppInput
                      label="Address 2"
                      value={buForm.address2}
                      onChange={(e) => setBuForm((f) => ({ ...f, address2: e.target.value }))}
                    />
                  </SimpleGrid>
                  <VillageSelect
                    isRequired
                    villages={villages}
                    value={buForm.villageId}
                    onChange={(villageId) => setBuForm((f) => ({ ...f, villageId }))}
                  />
                  <HStack justify="space-between">
                    <FormLabel mb={0}>Active</FormLabel>
                    <Switch
                      isChecked={buForm.active}
                      onChange={(e) => setBuForm((f) => ({ ...f, active: e.target.checked }))}
                      colorScheme="yellow"
                    />
                  </HStack>
                </VStack>
              ) : (
                <VStack align="stretch" spacing={4}>
                  <FormControl>
                    <FormLabel>BU Name</FormLabel>
                    <Box
                      px={4}
                      py={3}
                      bg="bg.input"
                      borderRadius="sm"
                      borderWidth="1px"
                      borderColor="border.subtle"
                    >
                      <Text fontWeight="600">
                        {bus.find((b) => b.buId === siteForm.buId)?.shortName || siteForm.buId}
                      </Text>
                    </Box>
                  </FormControl>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <AppInput
                      label="Site Name"
                      isRequired
                      placeholder="e.g. Pocket 1"
                      value={siteForm.siteName}
                      onChange={(e) => setSiteForm((f) => ({ ...f, siteName: e.target.value }))}
                    />
                    <AppInput
                      label="Project / Operation"
                      value={siteForm.projectOperation}
                      onChange={(e) =>
                        setSiteForm((f) => ({ ...f, projectOperation: e.target.value }))
                      }
                    />
                    <AppInput
                      label="Address 1"
                      value={siteForm.address1}
                      onChange={(e) => setSiteForm((f) => ({ ...f, address1: e.target.value }))}
                    />
                    <AppInput
                      label="Address 2"
                      value={siteForm.address2}
                      onChange={(e) => setSiteForm((f) => ({ ...f, address2: e.target.value }))}
                    />
                  </SimpleGrid>
                  <VillageSelect
                    isRequired
                    villages={villages}
                    value={siteForm.villageId}
                    onChange={(villageId) => setSiteForm((f) => ({ ...f, villageId }))}
                  />
                  <AppInput
                    label="PIN Code (optional override)"
                    placeholder="From Village Master / Manual entry"
                    value={siteForm.pinOverride}
                    onChange={(e) => setSiteForm((f) => ({ ...f, pinOverride: e.target.value }))}
                    helperText="Leave blank to use PIN from selected village."
                  />
                  <HStack justify="space-between">
                    <FormLabel mb={0}>Active</FormLabel>
                    <Switch
                      isChecked={siteForm.active}
                      onChange={(e) => setSiteForm((f) => ({ ...f, active: e.target.checked }))}
                      colorScheme="yellow"
                    />
                  </HStack>
                </VStack>
              )}
            </ModalBody>
            <ModalFooter gap={2}>
              <OutlineButton type="button" onClick={onClose} isDisabled={saving}>
                Cancel
              </OutlineButton>
              <PrimaryButton type="submit" isLoading={saving}>
                Save
              </PrimaryButton>
            </ModalFooter>
          </Box>
        </ModalContent>
      </Modal>
    </Box>
  );
}
